import { AxiosResponse } from "axios";
import {
  AltiusRes,
  CreateDealStageData,
  DealListViewConfig,
  DealStage,
  GetDealStagesData,
  IDealPatchData,
  PipelineViewConfig,
  ReorderDealStagesData,
  UpdateDealStageData
} from "store/types";

import { UserAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IPipelineActions {
  getDealStagesAction: <T extends boolean>(
    data: GetDealStagesData<T>
  ) => Promise<T extends true ? { data: DealStage[]; last_page: number } : Omit<DealStage, "deals" | "deals_count">[]>;
  createDealStageAction: (data: CreateDealStageData) => Promise<DealStage>;
  updateDealStageAction: (data: UpdateDealStageData) => Promise<DealStage>;
  deleteDealStageAction: (id: number) => Promise<void>;
  reorderDealStagesAction: (data: ReorderDealStagesData) => Promise<void>;
  updateDealStageItemAction: (data: IDealPatchData) => Promise<void>;
  updatePipelineViewConfigAction: (config: PipelineViewConfig) => Promise<void>;
  updateDealListViewConfigAction: (config: DealListViewConfig) => Promise<void>;
}

export class PipelineActions extends AsyncActions implements IPipelineActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public getDealStagesAction: IPipelineActions["getDealStagesAction"] = async <T extends boolean>(
    requestParams: GetDealStagesData<T>
  ) => {
    const {
      data: { data }
    } = await this.api.post<
      AltiusRes<T extends true ? { data: DealStage[]; last_page: number } : Omit<DealStage, "deals" | "deals_count">[]>
    >({
      url: "/api/v0.0.3/deals-pipeline/search",
      data: { ...requestParams }
    });

    return data;
  };

  public createDealStageAction: IPipelineActions["createDealStageAction"] = async (body) => {
    const {
      data: { data }
    }: AxiosResponse<AltiusRes<DealStage>> = await this.api.post({ url: "/api/v0.0.3/deals-pipeline", data: body });
    return data;
  };

  public updateDealStageAction: IPipelineActions["updateDealStageAction"] = async ({ id, ...body }) => {
    const {
      data: { data }
    }: AxiosResponse<AltiusRes<DealStage>> = await this.api.patch({
      url: `/api/v0.0.3/deals-pipeline/${id}`,
      data: body
    });
    return data;
  };

  public deleteDealStageAction: IPipelineActions["deleteDealStageAction"] = async (id) => {
    await this.api.delete({ url: `/api/v0.0.3/deals-pipeline/${id}` });
  };

  public reorderDealStagesAction: IPipelineActions["reorderDealStagesAction"] = async (data) => {
    await this.api.patch({ url: "/api/v0.0.3/deals-pipeline/reorder", data });
  };

  public updateDealStageItemAction: IPipelineActions["updateDealStageItemAction"] = async ({ dealId, ...body }) => {
    await this.api.patch({ url: `/api/v0.0.2/deals/${dealId}`, data: body });
  };

  public updatePipelineViewConfigAction: IPipelineActions["updatePipelineViewConfigAction"] = async (config) => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: { type: "pipelineViewConfig", key: "pipelineViewConfig", data: config }
    });
    this.dispatch({ type: UserAction.SetPipelineViewConfig, payload: config });
  };

  public updateDealListViewConfigAction: IPipelineActions["updateDealListViewConfigAction"] = async (config) => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: { type: "dealListViewConfig", key: "dealListViewConfig", data: config }
    });
    this.dispatch({ type: UserAction.SetDealListViewConfig, payload: config });
  };
}
